import React, { useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { X, Timer, Sparkles, MessageSquare, Loader2, CheckCircle2, Copy } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

const SEEN_KEY = "tcr_quick_quote_shown_v1";
const FALLBACK_DELAY_MS = 45_000; // mobile / no exit-intent fallback
const HIDE_ON_PATHS = ["/admin", "/quote", "/booking/success", "/booking/cancelled"];

function shouldSuppressOnPath(pathname) {
  return HIDE_ON_PATHS.some((p) => pathname.startsWith(p));
}

function synthEmail(phoneRaw) {
  const digits = (phoneRaw || "").replace(/\D/g, "") || `na${Date.now()}`;
  return `qq-${digits}@no-email.titancarrental.com`;
}

export default function QuickQuotePopup() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "", trip: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null); // { reference }

  const trigger = useCallback(() => {
    if (sessionStorage.getItem(SEEN_KEY)) return;
    if (shouldSuppressOnPath(location.pathname)) return;
    // Don't pile on top of Akua if she's already engaged in a chat
    const wingmanIntake = localStorage.getItem("titan_wingman_intake_state");
    if (wingmanIntake === "submitted") return;
    sessionStorage.setItem(SEEN_KEY, "1");
    setOpen(true);
  }, [location.pathname]);

  // Exit-intent (desktop) + fallback timer (mobile / engaged users)
  useEffect(() => {
    if (shouldSuppressOnPath(location.pathname)) return;
    if (sessionStorage.getItem(SEEN_KEY)) return;

    const onMouseLeave = (e) => {
      // Only fire when cursor exits via the TOP of the viewport (not side scrolls)
      if (e.clientY <= 4) trigger();
    };
    const fallbackId = window.setTimeout(trigger, FALLBACK_DELAY_MS);
    document.addEventListener("mouseleave", onMouseLeave);
    return () => {
      document.removeEventListener("mouseleave", onMouseLeave);
      window.clearTimeout(fallbackId);
    };
  }, [trigger, location.pathname]);

  const close = () => {
    setOpen(false);
  };

  const submit = async (e) => {
    e?.preventDefault();
    setError("");
    const name = form.name.trim();
    const phone = form.phone.trim();
    const email = form.email.trim();
    const trip = form.trip.trim();
    if (!name) return setError("Please share your name.");
    if (!phone && !email) return setError("Please share a phone or an email.");
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return setError("That email looks off.");
    if (!trip) return setError("Tell us one line about the trip.");

    setSubmitting(true);
    try {
      const payload = {
        name,
        email: email || synthEmail(phone),
        phone: phone || "Not provided",
        service_type: "self_drive",
        message: trip,
        website: "", // honeypot
      };
      const { data } = await api.post("/quote", payload);
      setResult({ reference: data.reference });

      // Seed Akua so she greets the visitor with their info already known
      try {
        localStorage.setItem(
          "titan_wingman_visitor",
          JSON.stringify({ name, email: email || "", phone })
        );
        localStorage.setItem("titan_wingman_intake_state", "submitted");
      } catch (e) {
        console.warn("QuickQuote: could not seed Akua localStorage", e);
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Could not send. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const copyRef = async () => {
    try {
      await navigator.clipboard.writeText(result.reference);
    } catch (e) {
      console.warn("QuickQuote: clipboard unavailable", e);
    }
  };

  const whatsappUrl = () => {
    const first = (form.name || "").split(" ")[0] || "there";
    const msg = `Hi Titan! I'm ${first}. I just sent a quick quote (${result?.reference || "TCQ"}): ${form.trip}`;
    return `https://wa.me/233244945582?text=${encodeURIComponent(msg)}`;
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-3 sm:p-6"
      data-testid="quick-quote-overlay"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="relative w-full max-w-md bg-white shadow-2xl border border-gray-200 animate-in slide-in-from-bottom-4 fade-in"
        data-testid="quick-quote-popup"
      >
        <button
          onClick={close}
          className="absolute top-3 right-3 p-1.5 text-gray-400 hover:text-[#0d2040] hover:bg-gray-100 z-10"
          data-testid="quick-quote-close"
          aria-label="Close"
        >
          <X size={18} />
        </button>

        {!result ? (
          <>
            {/* Header */}
            <div className="bg-[#0d2040] text-white p-5 pr-12">
              <div className="flex items-center gap-2 text-[0.65rem] uppercase tracking-widest text-[#e63946] font-bold">
                <Timer size={12} /> 60 seconds · Free quote
              </div>
              <h2 className="font-display text-2xl font-bold mt-2 leading-tight">
                Tell us about your trip — we&apos;ll text you a quote.
              </h2>
              <p className="text-white/70 text-sm mt-2">
                Used by Land Cruiser hires, weddings, airport runs, and 2-week corporate loans. No spam, ever.
              </p>
            </div>

            <form onSubmit={submit} noValidate className="p-5 space-y-3" data-testid="quick-quote-form">
              <div>
                <label className="block text-[0.65rem] uppercase tracking-widest text-gray-500 font-bold mb-1">Name</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  className="w-full px-3 py-2.5 text-sm border border-gray-300 focus:outline-none focus:border-[#e63946]"
                  placeholder="Your full name"
                  data-testid="quick-quote-name"
                  autoFocus
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[0.65rem] uppercase tracking-widest text-gray-500 font-bold mb-1">Phone *</label>
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                    className="w-full px-3 py-2.5 text-sm border border-gray-300 focus:outline-none focus:border-[#e63946]"
                    placeholder="024 494 5582"
                    data-testid="quick-quote-phone"
                  />
                </div>
                <div>
                  <label className="block text-[0.65rem] uppercase tracking-widest text-gray-500 font-bold mb-1">Email</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                    className="w-full px-3 py-2.5 text-sm border border-gray-300 focus:outline-none focus:border-[#e63946]"
                    placeholder="optional"
                    data-testid="quick-quote-email"
                  />
                </div>
              </div>
              <div className="text-[0.65rem] text-gray-500 -mt-1">Phone OR email is fine — whichever you check most.</div>

              <div>
                <label className="block text-[0.65rem] uppercase tracking-widest text-gray-500 font-bold mb-1">
                  Your trip — one sentence
                </label>
                <textarea
                  value={form.trip}
                  onChange={(e) => setForm((f) => ({ ...f, trip: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2.5 text-sm border border-gray-300 focus:outline-none focus:border-[#e63946] resize-none"
                  placeholder="e.g. Land Cruiser for a 3-day Kumasi wedding, Fri–Sun, with driver"
                  data-testid="quick-quote-trip"
                />
              </div>

              {error && (
                <div className="text-xs text-red-600 bg-red-50 border border-red-200 px-3 py-2" data-testid="quick-quote-error">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-[#e63946] hover:bg-[#c5202e] disabled:opacity-60 disabled:cursor-not-allowed text-white font-bold text-sm uppercase tracking-wider py-3 flex items-center justify-center gap-2"
                data-testid="quick-quote-submit"
              >
                {submitting ? (
                  <>
                    <Loader2 size={16} className="animate-spin" /> Sending…
                  </>
                ) : (
                  <>
                    <Sparkles size={15} /> Get my quote
                  </>
                )}
              </button>

              <div className="text-[0.65rem] uppercase tracking-widest text-center text-gray-400 pt-1">
                Average reply: under 12 minutes during opening hours
              </div>
            </form>
          </>
        ) : (
          <div className="p-8 text-center" data-testid="quick-quote-success">
            <CheckCircle2 className="mx-auto text-green-600" size={48} />
            <h2 className="font-display text-2xl font-bold text-[#0d2040] mt-4">Got it, {form.name.split(" ")[0]} — we&apos;re on it.</h2>
            <p className="text-gray-600 text-sm mt-2">
              A Titan host will reach out shortly. Your reference is below — keep it handy.
            </p>

            <div className="mt-5 inline-flex items-center gap-2 bg-[#f7f8fa] border border-gray-200 px-4 py-2.5">
              <span className="font-mono text-lg font-bold text-[#0d2040]" data-testid="quick-quote-reference">
                {result.reference}
              </span>
              <button
                onClick={copyRef}
                className="p-1 text-gray-500 hover:text-[#e63946]"
                title="Copy reference"
                data-testid="quick-quote-copy"
              >
                <Copy size={14} />
              </button>
            </div>

            <div className="mt-6 space-y-2">
              <a
                href={whatsappUrl()}
                target="_blank"
                rel="noreferrer noopener"
                className="w-full bg-[#25D366] hover:bg-[#1da851] text-white font-bold text-sm uppercase tracking-wider py-3 flex items-center justify-center gap-2"
                data-testid="quick-quote-whatsapp"
              >
                <MessageSquare size={15} /> Continue on WhatsApp
              </a>
              <button
                onClick={close}
                className="w-full text-xs uppercase tracking-widest text-gray-500 hover:text-[#0d2040] py-2"
                data-testid="quick-quote-dismiss-success"
              >
                Keep browsing
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
